// src/Layout.jsx

import React, { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';

import useMediaQuery from './hooks/useMediaQuery'; 

// Import all components and assets needed for the Layout
import StaggeredMenu from './components/StaggeredMenu/StaggeredMenu';
import PrismaticBurst from './components/PrismaticBurst/PrismaticBurst';
import kridaLogo from './assets/images/krida-logo.png';
import discordIcon from './assets/images/icons/discord-icon.svg';
import gmailIcon from './assets/images/icons/gmail-icon.svg';

// Menu data now lives in the Layout
const menuItems = [
  { label: 'Home', link: '/' },
  { label: 'About', link: '/about' },
  { label: 'SUTRA AI', link: '/sutra' },
  { label: 'Upcoming', link: '/upcoming' },
  { label: 'Contact', link: '/contact' },
  { label: 'Privacy', link: '/privacy' }
];

const socialItems = [
  { label: 'Gmail', link: 'mailto:info@krida.com?subject=Inquiry%20from%20the%20Krida%20Studio%20Website' },
  { label: 'Discord', link: 'https://discord.gg/2cPravBAu2' }
];

const Layout = () => {
  // Define a constant for header height to use in padding
  // This should match the visual height of your StaggeredMenu header
  const HEADER_HEIGHT = '100px'; // Adjust this value if your header is taller/shorter
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); 
  const isMobile = useMediaQuery('(max-width: 768px)'); 

  // 2. Add an effect to listen for scroll events
  useEffect(() => {
    const handleScroll = () => {
      // Check if scroll position is beyond 20px from the top
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []); // Run only once on mount

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    // Clean up on unmount or if isMenuOpen changes
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isMenuOpen]); // Dependency on isMenuOpen

  return (
    <div className="App">
      <div className="App-background">
        {!isMobile && (
          <PrismaticBurst
            animationType="rotate3d"
            intensity={1.5}
            speed={0.5}
            distort={0.8}
            mixBlendMode="lighten"
            colors={['#FF0000', '#FFA500', '#333333']}
          />
        )}
      </div>

      {/* The StaggeredMenu is now part of the global layout and fixed */}
      <StaggeredMenu
        logoUrl={kridaLogo}
        items={menuItems}
        socialItems={socialItems}
        menuButtonColor="#F5F5F5"
        openMenuButtonColor="#F5F5F5"
        changeMenuColorOnOpen={true}
        scrolled={isScrolled}
        onMenuOpen={() => setIsMenuOpen(true)} 
        onMenuClose={() => setIsMenuOpen(false)}
      />

      {/* --- MODIFIED: Added padding-top to push content below the fixed header --- */}
      <div className="App-foreground" style={{ paddingTop: HEADER_HEIGHT }}>
        <Outlet />

        <footer className="App-footer">
          <div className="footer-content">
            <div className="footer-column footer-logo-column">
              <img src={kridaLogo} alt="Krida Studio" className="footer-logo" />
              <p className="footer-copyright">© 2025 Krida Studio. All Rights Reserved.</p>
            </div>
            <div className="footer-column">
              <h4>Navigation</h4>
              <Link to="/">Home</Link>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
            <div className="footer-column">
              <h4>Connect</h4>
              <div className="footer-social-icons">
                <a href="https://discord.gg/2cPravBAu2" target="_blank" rel="noopener noreferrer"><img src={discordIcon} alt="Discord" /></a>
                <a href="mailto:info@krida.com?subject=Inquiry%20from%20the%20Krida%20Studio%20Website" target="_blank" rel="noopener noreferrer"><img src={gmailIcon} alt="Gmail" /></a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;